import {
  FETCH_OFFLINE_TOPUPS,
  FETCH_OFFLINE_TOPUPS_SUCCESS,
  FETCH_OFFLINE_TOPUPS_FAILED,
  SUBMIT_OFFLINE_TOPUP,
  SUBMIT_OFFLINE_TOPUP_SUCCESS,
  SUBMIT_OFFLINE_TOPUP_FAILED,
  APPROVE_OFFLINE_TOPUP,
  REJECT_OFFLINE_TOPUP,
  CLEAR_OFFLINE_TOPUP_MESSAGE,
  FETCH_OFFLINE_TOPUP_SETTINGS,
  FETCH_OFFLINE_TOPUP_SETTINGS_SUCCESS,
  FETCH_OFFLINE_TOPUP_SETTINGS_FAILED,
  EDIT_OFFLINE_TOPUP_SETTINGS,
  UPDATE_WALLET_BALANCE,
  UPDATE_WALLET_BALANCE_SUCCESS,
  UPDATE_WALLET_BALANCE_FAILED,
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { onValue, off, push, update, set, get } from "firebase/database";
import { ref as stRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { RequestPushMsg } from '../other/NotificationFunctions';

// ─── Driver: fetch own topup requests ─────────────────────────────────────────

export const fetchDriverOfflineTopups = (uid) => (dispatch) => {
  const { offlineTopupsByDriverRef } = firebase;

  dispatch({ type: FETCH_OFFLINE_TOPUPS, payload: null });

  onValue(offlineTopupsByDriverRef(uid), (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const arr = Object.keys(data).map((i) => {
        data[i].id = i;
        return data[i];
      });
      dispatch({
        type: FETCH_OFFLINE_TOPUPS_SUCCESS,
        payload: arr.sort((a, b) => b.createdAt - a.createdAt),
      });
    } else {
      dispatch({
        type: FETCH_OFFLINE_TOPUPS_SUCCESS,
        payload: [],
      });
    }
  }, (error) => {
    dispatch({ type: FETCH_OFFLINE_TOPUPS_FAILED, payload: error.message });
  });
};

export const stopFetchDriverOfflineTopups = (uid) => () => {
  const { offlineTopupsByDriverRef } = firebase;
  off(offlineTopupsByDriverRef(uid));
};

// ─── Admin: fetch all topup requests ──────────────────────────────────────────

export const fetchAllOfflineTopups = () => (dispatch) => {
  const { offlineTopupsRef } = firebase;

  dispatch({ type: FETCH_OFFLINE_TOPUPS, payload: null });

  onValue(offlineTopupsRef, (snapshot) => {
    if (snapshot.val()) {
      const data = snapshot.val();
      const arr = Object.keys(data).map((i) => {
        data[i].id = i;
        return data[i];
      });
      dispatch({
        type: FETCH_OFFLINE_TOPUPS_SUCCESS,
        payload: arr.sort((a, b) => b.createdAt - a.createdAt),
      });
    } else {
      dispatch({
        type: FETCH_OFFLINE_TOPUPS_SUCCESS,
        payload: [],
      });
    }
  });
};

export const stopFetchAllOfflineTopups = () => () => {
  const { offlineTopupsRef } = firebase;
  off(offlineTopupsRef);
};

// ─── Driver: submit a new topup request ───────────────────────────────────────

export const submitOfflineTopup = (topupData, proofImageUri) => async (dispatch) => {
  const { offlineTopupsRef, offlineProofImageRef } = firebase;

  dispatch({ type: SUBMIT_OFFLINE_TOPUP, payload: null });

  try {
    const newRef = push(offlineTopupsRef);
    const topupId = newRef.key;

    let proofImageUrl = null;
    if (proofImageUri) {
      const imageRef = offlineProofImageRef(topupData.uid, topupId);
      const response = await fetch(proofImageUri);
      const blob = await response.blob();
      await uploadBytes(imageRef, blob);
      proofImageUrl = await getDownloadURL(imageRef);
    }

    const entry = {
      ...topupData,
      proofImageUrl: proofImageUrl || null,
      status: 'review',
      createdAt: new Date().getTime(),
      processedAt: null,
      processedBy: null,
      rejectionReason: null,
    };

    await set(newRef, entry);

    dispatch({ type: SUBMIT_OFFLINE_TOPUP_SUCCESS, payload: null });
  } catch (error) {
    dispatch({
      type: SUBMIT_OFFLINE_TOPUP_FAILED,
      payload: error.message,
    });
  }
};

// ─── Admin: approve a topup request ───────────────────────────────────────────

export const approveOfflineTopup = (topup, adminUid) => async (dispatch) => {
  const {
    offlineTopupRef,
    walletHistoryRef,
    singleUserRef,
    settingsRef,
  } = firebase;

  dispatch({ type: APPROVE_OFFLINE_TOPUP, payload: topup.id });

  try {
    const settingsSnap = await get(settingsRef);
    const settings = settingsSnap.val();

    const userSnap = await get(singleUserRef(topup.uid));
    if (!userSnap.val()) throw new Error('User not found');

    const user = userSnap.val();
    const currentBalance = parseFloat(user.walletBalance) || 0;
    const newBalance = parseFloat(
      (currentBalance + parseFloat(topup.amount)).toFixed(settings.decimal)
    );

    const walletEntry = {
      type: 'Credit',
      amount: parseFloat(topup.amount),
      date: new Date().getTime(),
      txRef: 'OfflineTopup_' + topup.id,
    };

    await update(singleUserRef(topup.uid), { walletBalance: newBalance });
    await push(walletHistoryRef(topup.uid), walletEntry);
    await update(offlineTopupRef(topup.id), {
      status: 'approved',
      processedAt: new Date().getTime(),
      processedBy: adminUid,
    });

    if (user.pushToken) {
      RequestPushMsg(user.pushToken, {
        title: 'Wallet Top-up Approved',
        msg: 'Your wallet has been credited with ' + topup.amount + '.',
        screen: 'Wallet',
      });
    }

    dispatch({ type: UPDATE_WALLET_BALANCE_SUCCESS, payload: null });
  } catch (error) {
    dispatch({ type: UPDATE_WALLET_BALANCE_FAILED, payload: error.message });
  }
};

// ─── Admin: reject a topup request ────────────────────────────────────────────

export const rejectOfflineTopup = (topup, adminUid, reason) => async (dispatch) => {
  const { offlineTopupRef, singleUserRef } = firebase;

  dispatch({ type: REJECT_OFFLINE_TOPUP, payload: topup.id });

  try {
    await update(offlineTopupRef(topup.id), {
      status: 'rejected',
      processedAt: new Date().getTime(),
      processedBy: adminUid,
      rejectionReason: reason || null,
    });

    const userSnap = await get(singleUserRef(topup.uid));
    if (userSnap.val()?.pushToken) {
      RequestPushMsg(userSnap.val().pushToken, {
        title: 'Wallet Top-up Rejected',
        msg: reason
          ? 'Your top-up request was rejected: ' + reason
          : 'Your wallet top-up request was rejected.',
        screen: 'Wallet',
      });
    }
  } catch (error) {
    // rejection error is non-critical
  }
};

// ─── Clear message ─────────────────────────────────────────────────────────────

export const clearOfflineTopupMessage = () => (dispatch) => {
  dispatch({ type: CLEAR_OFFLINE_TOPUP_MESSAGE });
};

// ─── Offline topup settings ────────────────────────────────────────────────────

export const fetchOfflineTopupSettings = () => (dispatch) => {
  const { offlineTopupSettingsRef } = firebase;

  dispatch({ type: FETCH_OFFLINE_TOPUP_SETTINGS, payload: null });

  onValue(offlineTopupSettingsRef, (snapshot) => {
    dispatch({
      type: FETCH_OFFLINE_TOPUP_SETTINGS_SUCCESS,
      payload: snapshot.val() || null,
    });
  }, (error) => {
    dispatch({
      type: FETCH_OFFLINE_TOPUP_SETTINGS_FAILED,
      payload: error.message,
    });
  });
};

export const editOfflineTopupSettings = (settings) => async (dispatch) => {
  const { offlineTopupSettingsRef } = firebase;

  dispatch({ type: EDIT_OFFLINE_TOPUP_SETTINGS, payload: settings });
  await set(offlineTopupSettingsRef, settings);
};
